import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { AiTrackingOrigin, PromptButtonVariant } from '@databox/types/domains/ai/chat.enums';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export type ReportCreateOrigin = 'reports_new' | 'reports' | 'listing' | 'empty_screen';

const enum Choice {
	AI = 'ai',
	TEMPLATE = 'template',
	BLANK = 'blank',
}

interface Option {
	choice: Choice;
	title: string;
	description: string;
	buttonText: string;
	icon: IconName;
	footnote?: string;
}

const AI_PROMPT =
	"I want to create a new report as an Interactive HTML report (shareable page). Ask me a few questions about what it should cover, which data sources and metrics to show, and who it's for, then build it.";

const PREVIEW_SRC = '/images/reports/create-report/ai-report-preview.webp';

const COPY = {
	title: 'Create a report',
	cancel: 'Cancel',
	aiBadge: 'New',
	continue: 'Continue',
	classicTitle: 'Classic Report',
	classicDescription: 'Build it yourself in the report editor.',
} as const;

const OPTIONS: Record<Choice, Option> = {
	[Choice.AI]: {
		choice: Choice.AI,
		title: 'AI Artifact Report',
		description:
			'Genie picks the metrics, writes the summary and builds the charts from your data. Ask for changes in chat.',
		buttonText: 'Create AI report',
		icon: 'Genie',
		footnote: 'Saved to Artifacts in Genie',
	},
	[Choice.TEMPLATE]: {
		choice: Choice.TEMPLATE,
		title: 'Use a template',
		description: 'An account template or one of the public templates.',
		buttonText: 'Choose template',
		icon: 'FolderOpen',
	},
	[Choice.BLANK]: {
		choice: Choice.BLANK,
		title: 'Start blank',
		description: 'An empty page in the report editor.',
		buttonText: 'Start blank',
		icon: 'PencilRuler',
	},
};

export default class ReportsPopupCreateReportComponent extends PopupInnerModalComponent<PopupData.Report.Create> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked selectedChoice: Choice | null = null;

	copy = COPY;
	previewSrc = PREVIEW_SRC;
	aiOption = OPTIONS[Choice.AI];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.REPORT_CREATE_MODAL_SHOWN, {
			origin: this.popupData.origin,
		});
	}

	get classicOptions(): Option[] {
		return this.accountService.showPublicTemplates
			? [OPTIONS[Choice.TEMPLATE], OPTIONS[Choice.BLANK]]
			: [OPTIONS[Choice.BLANK]];
	}

	get primaryButtonText(): string {
		return this.selectedChoice ? OPTIONS[this.selectedChoice].buttonText : this.copy.continue;
	}

	get isConfirmDisabled(): boolean {
		return !this.selectedChoice;
	}

	private trackChoice(choice: Choice): void {
		this.analyticsService.event(TrackingEvent.REPORT_CREATE_MODAL_CHOICE, {
			choice,
			origin: this.popupData.origin,
		});
	}

	isSelected = (choice: Choice): boolean => this.selectedChoice === choice;

	@action
	select(choice: Choice): void {
		this.selectedChoice = choice;
	}

	@action
	onKeyDown(e: KeyboardEvent): void {
		if (e.key === 'Enter') {
			e.preventDefault();
			this.confirm();
		}
	}

	@action
	confirm(): void {
		const choice = this.selectedChoice;

		if (this.submitInProgress || !choice) {
			return;
		}

		this.submitInProgress = true;
		this.trackChoice(choice);
		this.close();

		switch (choice) {
			case Choice.AI:
				// Upgrade popup rather than a Genie with a disabled composer.
				// Running out of credits is handled by Genie itself.
				if (!this.billingService.checkIfCan(BillingFeature.AI_CHAT_ASSISTANT, true)) {
					break;
				}

				this.aiChatService.openGenieWithPrompt(
					AI_PROMPT,
					AiTrackingOrigin.REPORT_CREATE_MODAL,
					[],
					[],
					[],
					undefined,
					PromptButtonVariant.CREATE_ARTIFACT,
				);
				break;
			case Choice.TEMPLATE:
				this.navigationService.transitionToRoute('reports.public-templates');
				break;
			case Choice.BLANK:
				this.popupData.onCreateBlank();
				break;
		}
	}

	<template>
		<div class="content">
			<div class="header">
				<h3>{{this.copy.title}}</h3>
			</div>

			{{! template-lint-disable no-invalid-interactive }}
			<div
				class="options-container"
				role="radiogroup"
				aria-label={{this.copy.title}}
				{{on "keydown" this.onKeyDown}}
			>
				<div class="options-container__ai">
					<div
						class="option option--ai
							{{if (this.isSelected this.aiOption.choice) 'option--selected'}}"
						role="radio"
						aria-checked={{if (this.isSelected this.aiOption.choice) "true" "false"}}
						tabindex="0"
						{{on "focus" (fn this.select this.aiOption.choice)}}
						{{on "click" (fn this.select this.aiOption.choice)}}
					>
						<div class="option__body">
							<div class="option__title">
								<CommonUiIconComponent
									@name={{this.aiOption.icon}}
									@size="md"
									@gradient="orange-violet"
								/>
								{{this.aiOption.title}}
								<span class="badge2 badge2--blue">{{this.copy.aiBadge}}</span>
							</div>
							<p class="option__description">{{this.aiOption.description}}</p>
						</div>
						<div class="option__preview">
							<img
								class="option__image"
								src={{this.previewSrc}}
								alt=""
								width="500"
								height="3010"
							/>
						</div>
						<p class="option__footnote">{{this.aiOption.footnote}}</p>
					</div>
				</div>

				<div
					class="options-container__classic"
					role="group"
					aria-labelledby="create-report-classic-title"
				>
					<div class="option__body">
						<div class="option__title" id="create-report-classic-title">
							<CommonUiIconComponent @name="Reports" @size="md" />
							{{this.copy.classicTitle}}
						</div>
						<p class="option__description">{{this.copy.classicDescription}}</p>
					</div>

					{{#each this.classicOptions as |option|}}
						<div
							class="option option--compact
								{{if (this.isSelected option.choice) 'option--selected'}}"
							role="radio"
							aria-checked={{if (this.isSelected option.choice) "true" "false"}}
							tabindex="0"
							{{on "focus" (fn this.select option.choice)}}
							{{on "click" (fn this.select option.choice)}}
						>
							<div class="option__body">
								<div class="option__title">
									<CommonUiIconComponent @name={{option.icon}} @size="sm" />
									{{option.title}}
								</div>
								<p class="option__description">{{option.description}}</p>
							</div>
						</div>
					{{/each}}
				</div>
			</div>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text={{this.copy.cancel}}
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text={{this.primaryButtonText}}
						@isDisabled={{this.isConfirmDisabled}}
						@noDelay={{true}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.confirm}}
					/>
				</div>
			</div>
		</div>
	</template>
}
